export type IssueCellValue = string | number | boolean | null;

export type IssueRow = Record<string, IssueCellValue>;

const RESERVED_ISSUE_KEYS: ReadonlySet<string> = new Set(["kind", "__typename"]);

function isDisplayableCell(value: unknown): value is IssueCellValue {
  return (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
}

export function toIssueRows(
  issues: readonly Record<string, unknown>[] | null | undefined,
): IssueRow[] {
  if (!issues || issues.length === 0) {
    return [];
  }
  const rows: IssueRow[] = [];
  for (const issue of issues) {
    const row: IssueRow = {};
    for (const [key, value] of Object.entries(issue)) {
      if (!RESERVED_ISSUE_KEYS.has(key) && isDisplayableCell(value)) {
        row[key] = value;
      }
    }
    if (Object.keys(row).length > 0) {
      rows.push(row);
    } else if (typeof issue.kind === "string" && issue.kind) {
      // A CheckIssue subtype with no inline fragment in the query comes back as
      // only its discriminator; surface the kind so it degrades visibly instead
      // of vanishing. Add a `... on <Type>` fragment to the query to render its
      // fields properly.
      rows.push({ type: issue.kind });
    }
  }
  return rows;
}

/** Column order follows the first row's key order; rows are assumed
 *  homogeneous (one issue type per check). */
export function deriveIssueColumns(rows: readonly IssueRow[]): string[] {
  return rows.length > 0 ? Object.keys(rows[0]) : [];
}

/** Title-cases a snake_case or camelCase issue key for a column header. */
export function humanizeColumnKey(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

const deltaFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

/** Human-readable footing delta (thousands-separated, up to 2 decimals). A
 *  percentage footing's delta is a fraction (DDG parses `%` cells to fractions),
 *  so it is scaled and suffixed to match the addends and totals. */
export function formatDelta(delta: number, isPercentage = false): string {
  return isPercentage ? `${(delta * 100).toFixed(2)}%` : deltaFormatter.format(delta);
}

/** Displayable text for an issue cell; null renders as empty. */
export function formatIssueCell(value: IssueCellValue): string {
  return value === null ? "" : String(value);
}
